/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file balanceSlice.js
 * @description Redux slice for managing balance related state and operations
 */

import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import {getRequestParams} from "../async/get-fetch-params";
import {getAuthAccountId} from "../reselect/auth-selector";

/**
 * @function getInitialState
 * @description Creates the initial state object for the balance slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (balance = null, singleWalletBalance = null) => ({
  balance,
  error: null,
  loading: false,
  singleWalletBalance,
});

/**
 * @function loadBalance
 * @description Async thunk for load balance operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const loadBalance = createAsyncThunk("balance/load", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/acc/${accountId}/balance?originId=${originId}&lineId=${lineId}`);

    return { balance: result.data };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to load account balance", // serializable (err.response.data)
      name: "Load Account Balance Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function loadSingleWalletBalance
 * @description Async thunk for load single wallet balance operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const loadSingleWalletBalance = createAsyncThunk("balance/loadSingleWalletBalance", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/acc/${accountId}/singlewallet/bal?originId=${originId}&lineId=${lineId}`);

    return { singleWalletBalance: result.data };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to load account balance", // serializable (err.response.data)
      name: "Load Account Balance Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant balanceSlice
 * @description Redux slice for balance management
 */
const balanceSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(loadBalance.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(loadBalance.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(loadBalance.fulfilled, (state, action) => {
        state.balance = action.payload.balance;
        state.error = null;
        state.loading = false;
      })
      .addCase(loadSingleWalletBalance.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(loadSingleWalletBalance.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(loadSingleWalletBalance.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.singleWalletBalance = action.payload.singleWalletBalance;
      });
  },
  initialState: getInitialState(),
  name: "balance",
  reducers: {},
});
const { reducer } = balanceSlice;
export default reducer;
